import { useCallback, useEffect, useState } from "react";
import { Check, EyeOff, Loader2, Plus, Trash2 } from "lucide-react";
import { fetchAdminStories, addAdminStory, setStoryStatus, deleteStory, formatApiError } from "../../lib/api";

const TABS = ["pending", "approved", "hidden"];
const fmt = (iso) => new Date(iso).toLocaleDateString("en-AU", { dateStyle: "medium" });
const action = "inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors";

const StoryItem = ({ s, onStatus, onDelete }) => (
  <li data-testid="admin-story-item" className="border-t border-[#E6DEC6]/70 py-5 first:border-t-0">
    <p data-testid="admin-story-quote" className="whitespace-pre-line break-words font-serif text-lg text-[#2C221E]">“{s.quote}”</p>
    <p className="mt-2 text-sm text-[#C85A32]">— {s.display_name || "A Woven parent"}</p>
    <p className="mt-1 text-xs text-[#8A7A72]">{s.source === "admin" ? "Added by you" : s.email} · {fmt(s.created_at)}</p>
    <div className="mt-3 flex flex-wrap gap-2">
      {s.status !== "approved" && (
        <button onClick={() => onStatus(s.id, "approved")} data-testid="admin-story-approve-button" className={`${action} border-[#6E8576] text-[#2F4537] hover:bg-[#EAF0EC]`}><Check className="h-3.5 w-3.5" /> Approve</button>
      )}
      {s.status !== "hidden" && (
        <button onClick={() => onStatus(s.id, "hidden")} data-testid="admin-story-hide-button" className={`${action} border-[#D9A05B] text-[#6B4A1F] hover:bg-[#FAF3E8]`}><EyeOff className="h-3.5 w-3.5" /> Hide</button>
      )}
      <button onClick={() => onDelete(s.id)} data-testid="admin-story-delete-button" className={`${action} border-[#E6DEC6] text-[#B24E2A] hover:bg-[#F8EFEA]`}><Trash2 className="h-3.5 w-3.5" /> Delete</button>
    </div>
  </li>
);

const AddStory = ({ onAdd }) => {
  const [quote, setQuote] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    const q = quote.trim();
    if (!q) return setError("Please enter the quote.");
    if (q.length > 280) return setError("Please keep it to 280 characters or fewer.");
    setBusy(true);
    setError(await onAdd(q, name.trim()) || "");
    setBusy(false);
    setQuote("");
    setName("");
  };
  return (
    <form onSubmit={submit} noValidate data-testid="admin-add-story-form" className="mt-6 rounded-2xl border border-dashed border-[#E6DEC6] bg-[#FDFBF7] p-5">
      <p className="font-medium text-[#2C221E]">Add a story</p>
      <textarea rows={3} value={quote} onChange={(e) => setQuote(e.target.value)} placeholder="Quote" data-testid="admin-add-story-quote-input"
        className="mt-3 w-full resize-none rounded-2xl border border-[#E6DEC6] bg-white px-4 py-3 text-base outline-none focus:border-[#C85A32]" />
      <input value={name} maxLength={60} onChange={(e) => setName(e.target.value)} placeholder="Display name (optional)" data-testid="admin-add-story-name-input"
        className="mt-3 w-full rounded-full border border-[#E6DEC6] bg-white px-4 py-2.5 text-base outline-none focus:border-[#C85A32]" />
      {error && <p role="alert" data-testid="admin-add-story-error" className="mt-2 text-sm text-[#B24E2A]">{error}</p>}
      <button type="submit" disabled={busy} data-testid="admin-add-story-button" className="btn-soft mt-4">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Add story (approved)
      </button>
    </form>
  );
};

export const StoriesPanel = ({ token, onUnauthorized }) => {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("pending");
  const [error, setError] = useState("");

  const fail = useCallback((err) => (err.response?.status === 401 ? onUnauthorized() : setError(formatApiError(err.response?.data?.detail))), [onUnauthorized]);
  const load = useCallback(() => fetchAdminStories(token).then(({ data }) => setData(data)).catch(fail), [token, fail]);
  useEffect(() => { load(); }, [load]);

  const run = async (fn) => { setError(""); try { await fn(); } catch (err) { fail(err); } load(); };
  const onStatus = (id, status) => run(() => setStoryStatus(token, id, status));
  const onDelete = (id) => window.confirm("Delete this story permanently?") && run(() => deleteStory(token, id));
  const onAdd = async (quote, display_name) => {
    try {
      await addAdminStory(token, { quote, display_name: display_name || null });
      setTab("approved");
      load();
      return "";
    } catch (err) {
      if (err.response?.status === 401) onUnauthorized();
      return formatApiError(err.response?.data?.detail);
    }
  };

  const shown = data?.stories.filter((s) => s.status === tab) || [];
  return (
    <section data-testid="stories-panel" className="warm-card mt-10 p-6 sm:p-8 hover:translate-y-0">
      <p className="eyebrow">Family stories</p>
      <h2 className="mt-2 font-serif text-2xl text-[#2C221E]">Approve what appears on the website</h2>
      <div className="mt-5 flex flex-wrap gap-2" role="tablist">
        {TABS.map((t) => (
          <button key={t} role="tab" aria-selected={tab === t} onClick={() => setTab(t)} data-testid={`stories-tab-${t}`}
            className={`rounded-full px-4 py-2 text-sm font-semibold capitalize transition-colors ${tab === t ? "bg-[#2C221E] text-[#FDFBF7]" : "bg-[#F7F2E9] text-[#5C4F4A] hover:bg-[#EDE4D0]"}`}>
            {t} <span data-testid={`stories-count-${t}`}>({data?.counts[t] ?? 0})</span>
          </button>
        ))}
      </div>
      {error && <p role="alert" className="mt-3 text-sm text-[#B24E2A]">{error}</p>}
      <ul className="mt-4">
        {shown.map((s) => <StoryItem key={s.id} s={s} onStatus={onStatus} onDelete={onDelete} />)}
        {data && shown.length === 0 && <li data-testid="stories-empty" className="py-6 text-sm text-[#8A7A72]">No {tab} stories.</li>}
      </ul>
      <AddStory onAdd={onAdd} />
    </section>
  );
};
